import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import { School, Users, Zap, ArrowLeft, BookOpen, Crown, Mail, GraduationCap, TrendingUp, LifeBuoy, Share2 } from 'lucide-react';
import SupportModal from '../components/SupportModal';
import ShareModal from '../components/ShareModal'; // NUEVO

const formatARS = (amount) => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);
};

export default function SchoolAdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSupport, setShowSupport] = useState(false);
  const [showShare, setShowShare] = useState(false); // NUEVO
  const [billing, setBilling] = useState('monthly');

  useEffect(() => {
    if (user?.role !== 'school_admin') return;
    
    api.get('/school-admin/stats')
      .then(res => setData(res.data))
      .catch(err => toast.error('Error al cargar las métricas de la institución.'))
      .finally(() => setLoading(false));
  }, [user]);

  if (user?.role !== 'school_admin') {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-slate-700 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  const { institution, teachers, total_generations, subject_distribution, recent_dynamics } = data;
  const usagePercentage = Math.min(100, Math.round((institution.used_seats / institution.max_seats) * 100)) || 0;

  const subjectEntries = Object.entries(subject_distribution);
  const totalSubjects = subjectEntries.reduce((acc, curr) => acc + curr[1], 0);
  const chartColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#0ea5e9', '#14b8a6', '#64748b'];
  
  let currentAngle = 0;
  const conicGradientStr = totalSubjects === 0 
    ? '#1e293b 0% 100%' 
    : subjectEntries.map(([subj, count], i) => {
        const startAngle = currentAngle;
        const percentage = (count / totalSubjects) * 100;
        currentAngle += percentage;
        return `${chartColors[i % chartColors.length]} ${startAngle}% ${currentAngle}%`;
      }).join(', ');

  const B2B_PRICES = { small: 390000, medium: 950000, large: 1650000 };
  const getPrice = (tier) => {
    const base = B2B_PRICES[tier];
    if (billing === 'semiannual') return Math.round(base * 6 * 0.85);
    if (billing === 'annual') return Math.round(base * 12 * 0.75);
    return base;
  };

  return (
    <div className="min-h-screen bg-[#030712] text-slate-300 pb-20 font-sans relative">
      
      <header className="border-b border-slate-800 bg-slate-950 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2.5">
              {institution.logo_url ? (
                <img src={institution.logo_url} alt="Logo" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <School className="text-emerald-500" size={20} />
              )}
              <h1 className="text-white font-bold tracking-wide uppercase text-sm">
                Panel Directivo <span className="text-slate-500 mx-1">|</span> {institution.name}
              </h1>
            </div>
          </div>
          <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full font-black border border-emerald-500/20 uppercase tracking-widest">
            Acceso Total
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8 animate-fade-in-up">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Licencias Utilizadas</p>
                <p className="text-4xl font-black text-white">
                  {institution.used_seats} <span className="text-lg text-slate-500">/ {institution.max_seats}</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-slate-950 flex items-center justify-center text-blue-400 border border-slate-800">
                <Users size={24} />
              </div>
            </div>
            <div className="w-full bg-slate-950 rounded-full h-2.5 border border-slate-800 overflow-hidden">
              <div 
                className={`h-2.5 rounded-full ${usagePercentage >= 100 ? 'bg-red-500' : usagePercentage > 75 ? 'bg-amber-500' : 'bg-blue-500'}`} 
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>
            <p className="text-[10px] text-slate-500 font-bold mt-2 text-right">{usagePercentage}% de cupos ocupados</p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Dinámicas Generadas</p>
                <p className="text-4xl font-black text-white">{total_generations}</p>
                <button 
                  onClick={() => document.getElementById('registro-dinamicas').scrollIntoView({ behavior: 'smooth' })}
                  className="mt-3 flex items-center gap-1.5 text-xs font-bold text-violet-400 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <BookOpen size={14} /> Ver Registro
                </button>
              </div>
              <div className="w-12 h-12 rounded-xl bg-slate-950 flex items-center justify-center text-violet-400 border border-slate-800">
                <Zap size={24} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-1 space-y-8">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <h2 className="text-sm font-bold text-white mb-6 flex items-center gap-2 uppercase tracking-wider">
                <BookOpen size={16} className="text-emerald-400" /> Foco Curricular
              </h2>
              
              {subjectEntries.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-10">Aún no se han generado dinámicas para medir.</p>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="relative w-40 h-40 rounded-full flex items-center justify-center" 
                       style={{ background: `conic-gradient(${conicGradientStr})` }}>
                    <div className="w-28 h-28 bg-slate-900 rounded-full flex flex-col items-center justify-center">
                       <span className="text-2xl font-black text-white">{totalSubjects}</span>
                       <span className="text-[9px] text-slate-500 uppercase tracking-widest">Temas</span>
                    </div>
                  </div>

                  <div className="w-full mt-6 space-y-2.5">
                    {subjectEntries.map(([subject, count], index) => {
                      const percentage = Math.round((count / totalSubjects) * 100);
                      return (
                        <div key={subject} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 truncate">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: chartColors[index % chartColors.length] }}></span>
                            <span className="text-slate-300 truncate">{subject}</span>
                          </div>
                          <span className="text-slate-500 font-medium shrink-0 ml-2">{percentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gradient-to-b from-slate-900 to-slate-950 border border-amber-500/30 rounded-2xl p-6 shadow-2xl shadow-amber-900/10">
              <h2 className="text-sm font-bold text-white mb-2 flex items-center gap-2 uppercase tracking-wider">
                <Crown size={16} className="text-amber-400" /> Ampliar Licencia
              </h2>
              <p className="text-[11px] text-slate-400 mb-5 leading-relaxed">
                Asegurá el acceso a DinamiClass para todos tus docentes. Optimizá tu colegio hoy.
              </p>

              <div className="inline-flex items-center p-1 bg-slate-950 border border-slate-800 rounded-xl w-full mb-5">
                <button
                  onClick={() => setBilling('monthly')}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-bold transition-all ${billing === 'monthly' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-white'}`}
                >
                  Mes
                </button>
                <button
                  onClick={() => setBilling('semiannual')}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-bold transition-all ${billing === 'semiannual' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}
                >
                  6 Meses
                </button>
                <button
                  onClick={() => setBilling('annual')}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-bold transition-all ${billing === 'annual' ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:text-white'}`}
                >
                  Anual
                </button>
              </div>
              
              <div className="space-y-3 mb-5">
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="text-xs font-bold text-white">Pequeña <span className="text-slate-500 font-normal">(50)</span></p>
                  </div>
                  <p className="text-xs font-black text-amber-400">{formatARS(getPrice('small'))} <span className="text-[9px] font-normal text-slate-500">/{billing === 'monthly' ? 'mes' : billing === 'semiannual' ? 'semestre' : 'año'}</span></p>
                </div>
                <div className="bg-slate-950 border border-amber-500/30 rounded-lg p-3 flex justify-between items-center shadow-inner">
                  <div>
                    <p className="text-xs font-bold text-white">Mediana <span className="text-slate-500 font-normal">(175)</span></p>
                  </div>
                  <p className="text-xs font-black text-amber-400">{formatARS(getPrice('medium'))} <span className="text-[9px] font-normal text-slate-500">/{billing === 'monthly' ? 'mes' : billing === 'semiannual' ? 'semestre' : 'año'}</span></p>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="text-xs font-bold text-white">Grande <span className="text-slate-500 font-normal">(350)</span></p>
                  </div>
                  <p className="text-xs font-black text-amber-400">{formatARS(getPrice('large'))} <span className="text-[9px] font-normal text-slate-500">/{billing === 'monthly' ? 'mes' : billing === 'semiannual' ? 'semestre' : 'año'}</span></p>
                </div>
              </div>

              <a 
                href={`mailto:soporte@dinamiclassapp.com.ar?subject=Mejora de Licencia Institucional (${billing.toUpperCase()}) - ${institution.name}`}
                className="w-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-black uppercase tracking-widest text-[10px] py-3 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <Mail size={14} strokeWidth={2.5} /> Solicitar Cotización
              </a>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl flex flex-col h-[400px] overflow-hidden">
              <div className="p-6 border-b border-slate-800 bg-slate-950/50">
                <h2 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-wider">
                  <GraduationCap size={16} className="text-blue-400" /> Directorio Docente
                </h2>
                <p className="text-[10px] text-slate-500 mt-1">Nómina de profesores asociados a la licencia de tu colegio.</p>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                {teachers.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 py-10">
                    <Users size={40} className="mb-3 opacity-20" />
                    <p className="text-sm font-medium">Aún no se han registrado docentes.</p>
                    <p className="text-xs mt-1">Compartiles el código de tu escuela.</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {teachers.map((t, index) => (
                      <div key={t.id} className="p-4 rounded-xl hover:bg-slate-800/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-transparent hover:border-slate-800">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-950 border border-slate-700 flex items-center justify-center text-xs font-bold text-slate-400 shrink-0">
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">{t.name}</p>
                            <p className="text-xs text-slate-500">{t.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:items-end gap-1.5 ml-11 sm:ml-0">
                          <div className="flex gap-1 flex-wrap">
                            {t.levels && t.levels.length > 0 ? (
                              t.levels.map((lvl, i) => (
                                <span key={i} className="text-[9px] font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                  {lvl}
                                </span>
                              ))
                            ) : (
                              <span className="text-[9px] text-slate-600 uppercase">Sin nivel definido</span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 truncate max-w-[200px]" title={t.subjects?.join(', ')}>
                            {t.subjects && t.subjects.length > 0 ? t.subjects.join(', ') : 'Materias no especificadas'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div id="registro-dinamicas" className="bg-slate-900 border border-slate-800 rounded-2xl flex flex-col h-[400px] overflow-hidden scroll-mt-20">
              <div className="p-6 border-b border-slate-800 bg-slate-950/50">
                <h2 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-wider">
                  <Zap size={16} className="text-violet-400" /> Registro de Dinámicas
                </h2>
                <p className="text-[10px] text-slate-500 mt-1">Últimos temas planificados por tus docentes.</p>
              </div>
              <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                {!recent_dynamics || recent_dynamics.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500 py-10">
                    <BookOpen size={40} className="mb-3 opacity-20" />
                    <p className="text-sm font-medium">Aún no hay registros de actividades.</p>
                    <p className="text-xs mt-1 text-center px-4">Cuando tus docentes guarden una dinámica, vas a ver acá el tema y la materia.</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {recent_dynamics.map((dyn, idx) => (
                      <div key={idx} className="p-4 rounded-xl hover:bg-slate-800/50 transition-colors flex items-center justify-between border border-transparent hover:border-slate-800">
                        <div>
                          <p className="text-sm font-bold text-white truncate max-w-[200px] md:max-w-xs">{dyn.topic}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-blue-400 font-bold">{dyn.teacher_name}</span>
                            <span className="text-slate-700 text-[10px]">•</span>
                            <span className="text-[10px] text-slate-500">{new Date(dyn.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <span className="text-[9px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded uppercase tracking-wider whitespace-nowrap">
                          {dyn.subject}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* BOTONES FLOTANTES */}
      <div className="fixed bottom-6 right-4 md:right-8 flex flex-col gap-3 z-[60]">
        <button
          onClick={() => setShowShare(true)}
          className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-blue-900/50 hover:bg-blue-500 transition-all hover:scale-110 border border-blue-400/30"
          title="Compartir DinamiClass"
        >
          <Share2 size={20} />
        </button>
        <button
          onClick={() => setShowSupport(true)}
          className="w-12 h-12 bg-slate-800 text-slate-300 rounded-full flex items-center justify-center shadow-lg shadow-black/50 hover:bg-slate-700 hover:text-white border border-slate-700 transition-all hover:scale-110"
          title="Soporte y Ayuda"
        >
          <LifeBuoy size={20} />
        </button>
      </div>

      <SupportModal open={showSupport} onClose={() => setShowSupport(false)} />
      <ShareModal open={showShare} onClose={() => setShowShare(false)} />
    </div>
  );
}
